// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Mock service for the non-alarm ops surfaces (audit, log, floor plans).
// Deliberately async and query-shaped so real services can replace it without
// touching hooks or screens. All data comes from src/mocks/* — see
// docs/design/hardcoded-frontend-data.md.

import type { AuditEntry, FloorPlan, LogNote } from '../types/alarm';
import { LOG_NOTES } from '../mocks/log';
import { AUDIT_ENTRIES } from '../mocks/audit';
import { FLOOR_PLANS } from '../mocks/floorTemps';

/** Small artificial latency so loading states behave like a real API. */
const LATENCY_MS = 120;

function delay<T>(value: T): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), LATENCY_MS));
}

// In-memory mutable copy so add-note persists for the session.
const logNotes: LogNote[] = LOG_NOTES.map(n => ({ ...n }));

export interface AuditQuery {
  query?: string;
  actor?: string;
  sort?: 'time';
}

export async function getAuditEntries(params: AuditQuery = {}): Promise<AuditEntry[]> {
  const { query, actor } = params;
  let list = AUDIT_ENTRIES.map(e => ({ ...e }));
  if (actor && actor !== 'all') list = list.filter(e => e.who === actor);
  if (query) {
    const q = query.toLowerCase();
    list = list.filter(
      e =>
        e.text.toLowerCase().includes(q) ||
        e.whoLabel.toLowerCase().includes(q) ||
        (e.asset ?? '').toLowerCase().includes(q) ||
        (e.alarmId ?? '').toLowerCase().includes(q)
    );
  }
  return delay(list);
}

export async function getLogNotes(query?: string): Promise<LogNote[]> {
  let list = logNotes.map(n => ({ ...n }));
  if (query) {
    const q = query.toLowerCase();
    list = list.filter(
      n => n.text.toLowerCase().includes(q) || (n.asset ?? '').toLowerCase().includes(q)
    );
  }
  return delay(list);
}

export async function addLogNote(note: Omit<LogNote, 'id'>): Promise<LogNote> {
  const created: LogNote = { ...note, id: `log-${logNotes.length + 1}-${note.t}` };
  logNotes.unshift(created);
  return delay({ ...created });
}

export async function getFloorPlan(siteId: string): Promise<FloorPlan | null> {
  return delay(FLOOR_PLANS[siteId] ? { ...FLOOR_PLANS[siteId] } : null);
}
